// React 17 JSX transform — no import needed
import { useState } from "react";
import {
  Row, Col, Form, Input, Select, Button,
  AutoComplete,
} from "antd";
import {
  LoadingOutlined, ClockCircleOutlined, UpOutlined, DownOutlined, SearchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useSelector } from "react-redux";
import FeatureGate from "../../FeatureGate/FeatureGate";
import { contractOptions, stripEmoji } from "../../../shared/helpers";
import { CONTRACT_JOB, PERMANENT_JOB, PER_DIEM_JOB } from "../../../shared/constant/JobConstants";
import editjobicon from "../../../assets/img/editjob.png";
import deletejobicon from "../../../assets/img/deletejob.png";
import history from "../../../routes/history";
import { fetchToken } from "../../../API/API";
import { useCreateJobFormLogic } from "./useCreateJobFormLogic";
import { canUseSubscriptionFeatures } from "../../../shared/billing";
import "./CreateJobForm.css";

const { Option } = Select;
const { TextArea } = Input;

const DropdownLoader = () => (
  <div className="cj-dropdown-loader" role="status" aria-label="Loading">
    <LoadingOutlined spin />
  </div>
);

const DropdownEmpty = () => (
  <div className="cj-dropdown-empty">No data found</div>
);

const loadingDropdownProps = (loading, hasSearchValue) => ({
  notFoundContent: !hasSearchValue
    ? null
    : loading
      ? <DropdownLoader />
      : <DropdownEmpty />,
  dropdownRender: (menu) => loading && hasSearchValue ? <DropdownLoader /> : menu,
});


const noEnter = (e) => { if (e.key === "Enter") e.preventDefault(); };

const SectionHeader = ({ title, open, onToggle }) => (
  <>
    <div className="cj-section-header" onClick={onToggle}>
      <span className="cj-section-title">{title}</span>
      {open ? <UpOutlined /> : <DownOutlined />}
    </div>
    <div className="cj-section-divider" />
  </>
);

const Req = ({ text }) => <span style={{fontSize: '13px'}}>{text} <span className="cj-required">*</span></span>;

const getExperienceSortValue = (name) => {
  const match = String(name || '').match(/\d+/);
  return match ? parseInt(match[0], 10) : Number.MAX_SAFE_INTEGER;
};

const sortExperienceLevels = (levels) =>
  [...(levels || [])].sort(
    (a, b) => getExperienceSortValue(a.ExperienceLevelName) - getExperienceSortValue(b.ExperienceLevelName)
  );

export default function CreateJobForm() {
  const {
    form, formInitialValues, FromEdit, isCompletingIncompleteJob, fromSignUp, isHCUser, isNonHCUser,
    templateOpen, setTemplateOpen, projectOpen, setProjectOpen,
    jobDetailsOpen, setJobDetailsOpen, skillsOpen, setSkillsOpen,
    jobType, setJobType,
    gigtitle, setgigtitle, setgigduration,
    locationValue, setLocationValue, locobj, workpolicy, setworkpolicy,
    setOpenPositions, hoursPerPeriod, setHoursPerPeriod,
    hoursFrequency, setHoursFrequency, description, setDescription,
    descCharCount, setDescCharCount, saveAsTemplate, setSaveAsTemplate,
    jobVisibility, setJobVisibility,
    desiredExp, setDesiredExp, experienceLevels, experienceLevelsLoading,
    workAuthorization, setWorkAuthorization,
    taxTerm, setTaxTerm,
    skillDetails, setskillDetails, skillValue, setskillValue, tempskillValue, settempskillValue,
    percentage, setpercentage, mypercent, skillerror, skillDuplicateError,
    skillDecimalError, skillPercentValidator, skillNameError, skillErrorArray,
    addSkillInput, setAddSkillInput, addSkillBtn,
    industryDetails, setIndustryDetails, industryValue, setIndustryValue,
    percentage2, setPercentage2, mypercent2,
    addIndustryInput, setAddIndustryInput, addIndustryBtn,
    industryerror, industryDuplicateError, industryDecimalError,
    industryPercentValidator, industryErrorArray,
    industrydensity,
    skilldensity, skillsLoading, titleOptions, titleLoading, locationOptions, locationLoading,
    templates, templatesLoading, projects, projectsLoading, filters, industryLoading, setValidationCheck,
    canShowProjectDetails,
    isLoading, isPublishEnabled, setSelectedProjectId, addSkillPercent, editSkillsHandler, editSkills, deleteSkill,
    addIndustryPercent, editIndustryHandler, editIndustry, deleteIndustry,
    onSelectLocation, getSearchSkillsHandler, handleTitleSearch, handleLocationSearch,
    handleTemplateSelect, handlePublish,
    locationType, setLocationType, nlSearchText, setNlSearchText,
    nlAiLoading, nlAiTyping, handleNlWriteWithAi,
  } = useCreateJobFormLogic();

  const isPermanentJob = jobType === PERMANENT_JOB;
  const isUSALocation = locobj?.CountryName === "United States";
  const showNaturalLanguageSearch = canUseSubscriptionFeatures();

  const creditsBalance = useSelector((state) => state.plansReducer?.creditsBalance);
  const subscriptionFeaturesEnabled = canUseSubscriptionFeatures(
    JSON.parse(localStorage.getItem("userProfile") || "null")
  );
  const isPlanExpired =
    subscriptionFeaturesEnabled && creditsBalance?.data?.status === "expired";

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [visibilityDropdownOpen, setVisibilityDropdownOpen] = useState(false);
  const [findMatchesLoading, setFindMatchesLoading] = useState(false);
  const [industryOpen, setIndustryOpen] = useState(false);

  const handleFindMatches = async () => {
    setFindMatchesLoading(true);
    try {
      const headers = await fetchToken();
      const payload = {
        search: gigtitle || "",
        skillName: "",
        sortBy: "Most Relevant",
        page: 1,
        limit: 20,
        filters: {
          name: "",
          keyword: "",
          jobTitleInclude: [],
          jobTitleExclude: [],
          location: locationValue ? [locationValue] : [],
          currentWorkLocation: [],
          pastWorkLocation: [],
          skills: skillDetails.filter((s) => s.isDisable).map((s) => s.skillName),
          education: [],
          languages: [],
          yearsOfExperience: form.getFieldValue("desiredExp") || [],
          yearsOfExperienceInCurrentRole: [],
        },
      };
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/guest/associated-talents`,
        payload,
        { headers }
      );
      history.push("/managejobs", { findMatchesResult: response.data, findMatchesPayload: payload });
    } catch (err) {
      console.error("Find Matches error:", err);
    } finally {
      setFindMatchesLoading(false);
    }
  };

  if (isPlanExpired) {
    return (
      <FeatureGate
        featureName="Post a Job"
        iconType="candidate"
        description="Your free trial has ended. Choose a plan to continue posting jobs."
        bottomText="Post a Job is a paid feature and was given to you for free during your trial period."
      />
    );
  }

  return (
    <div className="cj-page">
      <div className="cj-page-header">
        <button type="button" className="cj-back-btn" onClick={() => history.goBack()}>←</button>
      </div>
      <div className="cj-container">

        <Form form={form} layout="vertical" initialValues={formInitialValues} requiredMark={false}>
          <div className="cj-section">
            {isCompletingIncompleteJob ? (
              <div className="cj-section-body" style={{ paddingTop: 20 }}>
                <h2 className="cj-page-title" style={{ marginLeft: 0, paddingTop: 0 }}>Complete Job Details</h2>
                <p className="cj-nl-search-desc" style={{ marginBottom: 0 }}>
                  Add the missing details for this job. This helps talent understand the opportunity and respond faster.
                </p>
              </div>
            ) : (
              <h2 className="cj-page-title">{FromEdit ? "Edit Job" : "Create New Job"}</h2>
            )}
          </div>

          <div className="cj-section">
            <SectionHeader title="Template and Visibility" open={templateOpen} onToggle={() => setTemplateOpen(!templateOpen)} />
            {templateOpen && (
              <div className="cj-section-body">
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Select Job Template" name="jobTemplate">
                      <Select
                        placeholder="Select"
                        className="cj-select"
                        suffixIcon={templatesLoading ? <LoadingOutlined spin /> : <DownOutlined />}
                        popupClassName="cj-tv-popup"
                        loading={templatesLoading}
                        open={templateDropdownOpen}
                        onDropdownVisibleChange={(v) => setTemplateDropdownOpen(v)}
                        allowClear
                        onClear={() => setSelectedTemplateId(null)}
                        dropdownRender={() => (
                          <div className="cj-tv-dropdown">
                            {templatesLoading ? (
                              <DropdownLoader />
                            ) : Array.isArray(templates) && templates.length > 0 ? (
                              templates.map((t, i) => (
                                <div
                                  key={i}
                                  className={`cj-tv-tpl-item${selectedTemplateId === t.JobOrderId ? " active" : ""}`}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setSelectedTemplateId(t.JobOrderId);
                                    handleTemplateSelect(t.JobOrderId);
                                    form.setFieldsValue({ jobTemplate: t.JobOrderId });
                                    setTemplateDropdownOpen(false);
                                  }}
                                >
                                  {t.Title}
                                </div>
                              ))
                            ) : (
                              <DropdownEmpty />
                            )}
                          </div>
                        )}
                      >
                        {Array.isArray(templates) && templates.map((t, i) => (
                          <Option key={i} value={t.JobOrderId}>{t.Title}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Job Visibility" name="jobVisibility">
                      <Select
                        className="cj-select"
                        suffixIcon={<DownOutlined />}
                        popupClassName="cj-tv-popup"
                        open={visibilityDropdownOpen}
                        onDropdownVisibleChange={(v) => setVisibilityDropdownOpen(v)}
                        dropdownRender={() => (
                          <div className="cj-tv-dropdown">
                            <div
                              className={`cj-tv-vis-item${jobVisibility === "public" ? " active" : ""}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setJobVisibility("public");
                                form.setFieldsValue({ jobVisibility: "public" });
                                setVisibilityDropdownOpen(false);
                              }}
                            >
                              <div className="cj-tv-vis-title">Public Job</div>
                              <div className="cj-tv-vis-desc">Job will show to all internal and external talents.</div>
                            </div>
                            <div
                              className={`cj-tv-vis-item${jobVisibility === "private" ? " active" : ""}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setJobVisibility("private");
                                form.setFieldsValue({ jobVisibility: "private" });
                                setVisibilityDropdownOpen(false);
                              }}
                            >
                              <div className="cj-tv-vis-title">Private Job</div>
                              <div className="cj-tv-vis-desc">Job will show only to the internal talents.</div>
                            </div>
                          </div>
                        )}
                      >
                        <Option value="public">Public</Option>
                        <Option value="private">Private</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            )}
          </div>

          {canShowProjectDetails && (
            <div className="cj-section">
              <SectionHeader title="Project Details" open={projectOpen} onToggle={() => setProjectOpen(!projectOpen)} />
              {projectOpen && (
                <div className="cj-section-body">
                  <Form.Item label="Select Project" name="project">
                    <Select
                      placeholder="Select"
                      className="cj-select"
                      suffixIcon={projectsLoading ? <LoadingOutlined spin /> : <DownOutlined />}
                      popupClassName="post-job-form-dropdowns"
                      loading={projectsLoading}
                      notFoundContent={projectsLoading ? <DropdownLoader /> : <DropdownEmpty />}
                      onChange={(value) => setSelectedProjectId(value || null)}
                      allowClear
                    >
                      {Array.isArray(projects) && projects.map((project) => (
                        <Option key={project._id} value={project._id}>
                          {project.projectName}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              )}
            </div>
          )}

          {/* Job Details */}
          <div className="cj-section" id="cj-job-details-section">
            <SectionHeader title="Job Details" open={jobDetailsOpen} onToggle={() => setJobDetailsOpen(!jobDetailsOpen)} />
            {jobDetailsOpen && (
              <div className="cj-section-body">
                <Form.Item label="Job Type" className="cj-field-jobtype">
                  <div className="cj-job-type-group">
                    {[
                      { label: "Contract", value: CONTRACT_JOB },
                      { label: "Permanent", value: PERMANENT_JOB },
                      ...(isHCUser ? [{ label: "Per Diem", value: PER_DIEM_JOB }] : []),
                    ].map(({ label, value }) => (
                      <button key={value} type="button"
                        className={`cj-job-type-btn${jobType === value ? " active" : ""}`}
                        onClick={() => setJobType(value)}>
                        {label}
                      </button>
                    ))}
                  </div>
                </Form.Item>

                {/* Row 1 — Contract: Job Title | Job Duration   Permanent: Job Title | Job Location */}
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item label={<Req text="Job Title" />} name="gigtitle"
                      rules={[{ required: true, message: "Please add a job title" }]}>
                      <AutoComplete options={titleOptions} onSearch={handleTitleSearch}
                        onSelect={(val) => setgigtitle(val)} onClear={() => setgigtitle("")}
                        filterOption={(i, o) => o.value.toUpperCase().indexOf(i.toUpperCase()) !== -1}
                        popupClassName="post-job-form-dropdowns"
                        {...loadingDropdownProps(titleLoading, Boolean(gigtitle?.trim()))}>
                        <Input className="cj-input" placeholder="Enter job title" allowClear value={gigtitle}
                          onChange={(e) => setgigtitle(e.target.value.trim())} onKeyDown={noEnter} />
                      </AutoComplete>
                    </Form.Item>
                  </Col>

                  {!isPermanentJob ? (
                    <Col xs={24} sm={12}>
                      <Form.Item label={<Req text="Job Duration" />} name="JobDuration"
                        rules={[{ required: true, message: "Choose the job duration" }]}>
                        <Select placeholder="Select duration" className="cj-select" suffixIcon={<DownOutlined />}
                          onChange={(v) => setgigduration(v)} popupClassName="post-job-form-dropdowns">
                          {contractOptions?.map((item) => (
                            <Option key={item.IdealContractLengthId} value={item.IdealContractLengthName}>
                              {item.IdealContractLengthName}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  ) : workpolicy !== "fully-remote" ? (
                    <Col xs={24} sm={24}>
                      <div className="cj-location-split">
                        <div className="cj-location-split-col">
                          <label className="cj-location-split-label">
                            Job Type <span className="cj-required">*</span>
                          </label>
                          <Select
                            className="cj-select cj-location-type-select"
                            suffixIcon={<DownOutlined />}
                            popupClassName="post-job-form-dropdowns"
                            value={locationType}
                            onChange={(v) => setLocationType(v)}
                          >
                            <Option value="Onsite">Onsite</Option>
                            <Option value="Remote">Remote</Option>
                            <Option value="Hybrid">Hybrid</Option>
                          </Select>
                        </div>
                        {locationType !== "Remote" && (
                          <div className="cj-location-split-col">
                            <Form.Item label={<Req text="Job Location" />} name="locationValuee"
                              rules={[{ required: true, message: "Please enter a job location" }]}
                              style={{ marginBottom: 0 }}>
                              <AutoComplete options={locationOptions} onSelect={onSelectLocation}
                                listHeight={160} popupClassName="post-job-form-dropdowns"
                                {...loadingDropdownProps(locationLoading, Boolean(locationValue?.trim()))}>
                                <Input className="cj-input" placeholder="City, state or country" value={locationValue}
                                  onChange={(e) => { const val = e.target.value.trim(); setLocationValue(val); setValidationCheck(false); handleLocationSearch(val); }}
                                  onKeyDown={noEnter} />
                              </AutoComplete>
                            </Form.Item>
                          </div>
                        )}
                      </div>
                    </Col>
                  ) : null}
                </Row>

                {/* Row 2 — No of Positions | Desired Years of Experience */}
                <Row gutter={24}>
                  {!isPermanentJob && workpolicy !== "fully-remote" && (
                    <Col xs={24} sm={24}>
                      <div className="cj-location-split">
                        <div className="cj-location-split-col">
                          <label className="cj-location-split-label">
                            Job Type <span className="cj-required">*</span>
                          </label>
                          <Select
                            className="cj-select cj-location-type-select"
                            suffixIcon={<DownOutlined />}
                            popupClassName="post-job-form-dropdowns"
                            value={locationType}
                            onChange={(v) => setLocationType(v)}
                          >
                            <Option value="Onsite">Onsite</Option>
                            <Option value="Remote">Remote</Option>
                            <Option value="Hybrid">Hybrid</Option>
                          </Select>
                        </div>
                        {locationType !== "Remote" && (
                          <div className="cj-location-split-col">
                            <Form.Item label={<Req text="Job Location" />} name="locationValuee"
                              rules={[{ required: true, message: "Please enter a job location" }]}
                              style={{ marginBottom: 0 }}>
                              <AutoComplete options={locationOptions} onSelect={onSelectLocation}
                                listHeight={160} popupClassName="post-job-form-dropdowns"
                                {...loadingDropdownProps(locationLoading, Boolean(locationValue?.trim()))}>
                                <Input className="cj-input" placeholder="City, state or country" value={locationValue}
                                  onChange={(e) => { const val = e.target.value.trim(); setLocationValue(val); setValidationCheck(false); handleLocationSearch(val); }}
                                  onKeyDown={noEnter} />
                              </AutoComplete>
                            </Form.Item>
                          </div>
                        )}
                      </div>
                    </Col>
                  )}

                  {isHCUser && (
                    <Col xs={24} sm={12}>
                      <Form.Item label={<Req text="Workplace Policy" />} name="workpolicyy"
                        rules={[{ required: true, message: "Please add workplace policy" }]}>
                        <Select placeholder="Select" className="cj-select" suffixIcon={<DownOutlined />}
                          popupClassName="post-job-form-dropdowns" onChange={(v) => setworkpolicy(v)}>
                          <Option value="fully-remote">Fully remote</Option>
                          <Option value="hybrid">Hybrid</Option>
                          <Option value="on-prem">On Premises</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  )}

                  <Col xs={24} sm={12}>
                    <Form.Item label={<Req text="No of Positions" />} name="Positions"
                      rules={[{ required: true, message: "Please enter the number of positions" }]}>
                      <Input type="number" placeholder="2" className="cj-input"
                        onKeyDown={(e) => ["-", "+", "e", "E"].includes(e.key) && e.preventDefault()}
                        onWheel={(e) => e.target.blur()}
                        onChange={(e) => setOpenPositions(parseInt(e.target.value))} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item label={<Req text="Desired Years of Experience" />} name="desiredExp"
                      rules={[{ required: true, message: "Please select years of experience" }]}>
                      <Select mode="multiple" placeholder="Select desired experience" className="cj-select"
                        suffixIcon={experienceLevelsLoading ? <LoadingOutlined spin /> : <DownOutlined />}
                        popupClassName="post-job-form-dropdowns"
                        loading={experienceLevelsLoading}
                        notFoundContent={experienceLevelsLoading ? <DropdownLoader /> : <DropdownEmpty />}
                        onChange={(vals) => setDesiredExp(vals)}>
                        {sortExperienceLevels(experienceLevels).map((item) => (
                          <Option key={item.ExperienceLevelId} value={item.ExperienceLevelId}>
                            {item.ExperienceLevelName === "> 10 Years" ? "More than 10 years" : item.ExperienceLevelName}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {/* Row 3 — Work Authorization | Tax Term */}
                {isUSALocation && (
                <Row gutter={24}>
                  {workpolicy !== "fully-remote" && (
                    <Col xs={24} sm={12}>
                      <Form.Item label={<Req text="Work Authorization" />} name="WorkAuthorization"
                        rules={[{ required: true, message: "Choose work authorization" }]}>
                        <Select mode="multiple" placeholder="Choose your work authorization" className="cj-select"
                          suffixIcon={<DownOutlined />} popupClassName="post-job-form-dropdowns"
                          onChange={(vals) => setWorkAuthorization(vals)}>
                          <Option value="US Resident/Citizen">US Resident/Citizen</Option>
                          <Option value="EAD">EAD</Option>
                          <Option value="H1-B">H1-B</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  )}

                  <Col xs={24} sm={12}>
                    <Form.Item label={<Req text="Tax Term" />} name="IdealTerms"
                      rules={[{ required: true, message: "Select tax term" }]}>
                      <Select mode="multiple" placeholder="Select tax term" className="cj-select"
                        suffixIcon={<DownOutlined />} popupClassName="post-job-form-dropdowns"
                        onChange={(vals) => setTaxTerm(vals)}>
                        <Option value="W-2">W-2</Option>
                        <Option value="1099/Corp-to-Corp">1099/Corp-to-Corp</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                )}

                <Form.Item label={<Req text="Job Description" />} name="Description"
                  rules={[{ required: true, message: "Please add a job description" }]}>
                  <div className="cj-desc-wrapper">
                    <TextArea rows={5} placeholder="Enter here..." maxLength={5000} value={description}
                      className="cj-description-input"
                      onChange={(e) => { const val = stripEmoji(e.target.value); setDescription(val); setDescCharCount(val.length); form.setFieldsValue({ Description: val }); }} />
                    <div className="cj-char-count">{descCharCount}/5000</div>
                  </div>
                </Form.Item>
              </div>
            )}
          </div>

          {/* Skills */}
          <div className="cj-section">
            <SectionHeader title="Skills" open={skillsOpen} onToggle={() => setSkillsOpen(!skillsOpen)} />
            {skillsOpen && (
              <div className="cj-section-body">
                <p className="cj-skill-hint">Total skill percent should be 100%.</p>

                {skillDetails.map((item, index) => (
                  <div className="cj-skill-row" key={index}>
                    <div className="cj-skill-fields">
                      <div className="cj-skill-name-col">
                        <AutoComplete options={skilldensity} onSelect={(v) => setskillValue(v)}
                          defaultValue={item?.skillName || null} disabled={item?.isDisable}
                          popupClassName="post-job-form-dropdowns"
                          {...loadingDropdownProps(skillsLoading, Boolean(skillValue?.trim()))}
                          filterOption={(i, o) => o.value.toUpperCase().indexOf(i.toUpperCase()) !== -1}
                          style={{ width: "100%" }}>
                          <Input className={skillNameError && !item?.isDisable ? "percentInputError" : "cj-input"}
                            placeholder="Skill" value={!item?.isDisable ? skillValue : undefined}
                            onChange={(e) => { setskillValue(e.target.value); getSearchSkillsHandler(e); }}
                            disabled={item?.isDisable} onKeyDown={noEnter} />
                        </AutoComplete>
                      </div>
                      <div className="cj-skill-pct-col">
                        <Input className={`cj-input${skillerror && skillErrorArray[index] ? " percentInputError" : ""}`}
                          placeholder="%" defaultValue={item?.SkillPercent}
                          type="number"
                          min={0}
                          max={100}
                          suffix="%"
                          onChange={(e) => {
                            setpercentage(e.target.value.trim());
                            const temp = [...skillDetails];
                            temp[index] = { ...temp[index], SkillPercent: parseInt(e.target.value), isDisable: false };
                            setskillDetails(temp);
                          }}
                          disabled={item?.isDisable} onKeyDown={noEnter} />
                      </div>
                    </div>
                    {item.isDisable ? (
                      <div className="cj-skill-actions">
                        <img src={editjobicon} alt="edit" className="cj-action-icon" onClick={() => editSkillsHandler(index, item)} />
                        <img src={deletejobicon} alt="delete" className="cj-action-icon" onClick={() => deleteSkill(item, index)} />
                      </div>
                    ) : (
                      <div className="contractgig-add-btn" onClick={() => editSkills(index)}>ADD</div>
                    )}
                  </div>
                ))}

                {mypercent < 100 && addSkillInput === "block" && (
                  <div className="cj-skill-input-group">
                    <div className="cj-skill-name-col">
                      <AutoComplete options={skilldensity} onSelect={(v) => { setskillValue(v); settempskillValue(""); }}
                        value={skillValue} filterOption={false}
                        popupClassName="post-job-form-dropdowns" style={{ width: "100%" }}
                        {...loadingDropdownProps(skillsLoading, Boolean(skillValue?.trim()))}>
                        <Input className={skillNameError ? "percentInputError" : "cj-input"}
                          placeholder="Skill" value={skillValue}
                          onChange={(e) => { setskillValue(e.target.value); getSearchSkillsHandler(e); }}
                          onKeyDown={noEnter} />
                      </AutoComplete>
                    </div>
                    <div className="cj-skill-pct-col">
                      <Input placeholder="%" className="cj-input" value={percentage}
                        type="number"
                        min={0}
                        max={100}
                        suffix="%"
                        onChange={(e) => setpercentage(e.target.value.trim())} onKeyDown={noEnter} />
                    </div>
                    <div className="cj-skill-add-action">
                      <button type="button" className="cj-add-btn" onClick={addSkillPercent}>
                        <span className="cj-add-plus">+</span> Add
                      </button>
                    </div>
                  </div>
                )}

                {skillerror && <div className="cj-error-msg">Total cannot be more than 100%</div>}
                {skillDecimalError && <div className="cj-error-msg">Skill density percentage should be a whole number</div>}
                {skillDuplicateError && <div className="cj-error-msg">Duplicate skill names not allowed</div>}
                {skillPercentValidator && mypercent < 100 && <div className="cj-error-msg">Total must be 100%</div>}

                {mypercent < 100 && skillDetails?.length > 0 && skillDetails.every((el) => el.isDisable) && (
                  <div style={{ display: addSkillBtn }}>
                    <Button className="cj-add-new-skill-btn" onClick={() => {
                      setskillDetails((prev) => prev.map((el) => ({ ...el, isDisable: true })));
                      setAddSkillInput("block");
                      setpercentage("");
                    }}>
                      <Row><Col className="plus-Icon">+</Col><Col className="btn-Text">Add a new Skill</Col></Row>
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {showNaturalLanguageSearch && !isCompletingIncompleteJob && (
            <div className="cj-section cj-nl-search-section">
              <div className="cj-nl-search-header">
                <SearchOutlined className="cj-nl-search-icon" />
                <span className="cj-nl-search-title">Natural Language Search</span>
              </div>
              <p className="cj-nl-search-desc">
                Describe talent nice-to-haves in as much detail as possible so Skillgigs can source top matches.
                For any absolute must-haves, you can use filters or booleans on the next page.
              </p>
              <div className="cj-nl-search-box">
                {nlAiLoading ? (
                  <div className="cj-nl-search-skeleton">
                    <div className="cj-nl-search-skeleton-line" />
                    <div className="cj-nl-search-skeleton-line cj-nl-search-skeleton-line-mid" />
                    <div className="cj-nl-search-skeleton-line cj-nl-search-skeleton-line-short" />
                  </div>
                ) : (
                  <textarea
                    className={`cj-nl-search-textarea${nlAiTyping ? " cj-nl-search-textarea-typing" : nlSearchText ? " cj-nl-search-textarea-done" : ""}`}
                    placeholder={"e.g. Experience in Fintech or SaaS startups\nHas mentored Junior Engineers and is an expert at refactoring code\nCommunicates well with C-suite executives"}
                    value={nlSearchText}
                    readOnly={nlAiTyping}
                    onChange={(e) => { if (!nlAiTyping) setNlSearchText(stripEmoji(e.target.value)); }}
                  />
                )}
                {!nlSearchText.trim() && !nlAiLoading && !nlAiTyping && (
                  <button type="button" className="cj-nl-search-ai-btn" onClick={handleNlWriteWithAi}>
                    <span className="cj-nl-search-ai-icon">✦</span> Write with AI
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Industry — NonHC users only */}
          {isNonHCUser && (
            <div className="cj-section">
              <SectionHeader title="Industry" open={industryOpen} onToggle={() => setIndustryOpen(!industryOpen)} />
              {industryOpen && <div className="cj-section-body">
                <p className="cj-skill-hint">Total industry percent should be 100%.</p>

                {industryDetails.map((item, index) => (
                  <div className="cj-skill-row" key={index}>
                    <div className="cj-skill-fields">
                      <div className="cj-skill-name-col">
                        <AutoComplete
                          options={industrydensity}
                          onSelect={(v) => setIndustryValue(v)}
                          defaultValue={item?.industryName || null}
                          disabled={item?.isDisable}
                          popupClassName="post-job-form-dropdowns"
                          {...loadingDropdownProps(industryLoading, Boolean(industryValue?.trim()))}
                          filterOption={(i, o) => o.value.toUpperCase().indexOf(i.toUpperCase()) !== -1}
                          style={{ width: "100%" }}
                        >
                          <Input
                            className="cj-input"
                            placeholder="Industry"
                            value={!item?.isDisable ? industryValue : undefined}
                            onChange={(e) => setIndustryValue(e.target.value.trim())}
                            disabled={item?.isDisable}
                            onKeyDown={noEnter}
                          />
                        </AutoComplete>
                      </div>
                      <div className="cj-skill-pct-col">
                        <Input
                          className={`cj-input${industryerror && industryErrorArray[index] ? " percentInputError" : ""}`}
                          placeholder="%"
                          defaultValue={item?.industryPercent}
                          type="number"
                          min={0}
                          max={100}
                          suffix="%"
                          onChange={(e) => {
                            setPercentage2(e.target.value.trim());
                            const temp = [...industryDetails];
                            temp[index] = { ...temp[index], industryPercent: parseInt(e.target.value), isDisable: false };
                            setIndustryDetails(temp);
                          }}
                          disabled={item?.isDisable}
                          onKeyDown={noEnter}
                        />
                      </div>
                    </div>
                    {item.isDisable ? (
                      <div className="cj-skill-actions">
                        <img src={editjobicon} alt="edit" className="cj-action-icon" onClick={() => editIndustryHandler(index, item)} />
                        <img src={deletejobicon} alt="delete" className="cj-action-icon" onClick={() => deleteIndustry(item, index)} />
                      </div>
                    ) : (
                      <div className="contractgig-add-btn" onClick={() => editIndustry(index)}>ADD</div>
                    )}
                  </div>
                ))}

                {mypercent2 < 100 && addIndustryInput === "block" && (
                  <div className="cj-skill-input-group">
                    <div className="cj-skill-name-col">
                      <AutoComplete
                        options={industrydensity}
                        onSelect={(v) => { setIndustryValue(v); }}
                        value={industryValue}
                        filterOption={false}
                        popupClassName="post-job-form-dropdowns"
                        style={{ width: "100%" }}
                        {...loadingDropdownProps(industryLoading, Boolean(industryValue?.trim()))}
                      >
                        <Input
                          className="cj-input"
                          placeholder="Industry"
                          value={industryValue}
                          onChange={(e) => setIndustryValue(e.target.value.trim())}
                          onKeyDown={noEnter}
                        />
                      </AutoComplete>
                    </div>
                    <div className="cj-skill-pct-col">
                      <Input
                        placeholder="%"
                        className="cj-input"
                        value={percentage2}
                        type="number"
                        min={0}
                        max={100}
                        suffix="%"
                        onChange={(e) => setPercentage2(e.target.value.trim())}
                        onKeyDown={noEnter}
                      />
                    </div>
                    <div className="cj-skill-add-action">
                      <button type="button" className="cj-add-btn" onClick={addIndustryPercent}>
                        <span className="cj-add-plus">+</span> Add
                      </button>
                    </div>
                  </div>
                )}

                {industryerror && <div className="cj-error-msg">Total cannot be more than 100%</div>}
                {industryDecimalError && <div className="cj-error-msg">Industry density percentage should be a whole number</div>}
                {industryDuplicateError && <div className="cj-error-msg">Duplicate industry names not allowed</div>}
                {industryPercentValidator && mypercent2 < 100 && <div className="cj-error-msg">Total must be 100%</div>}

                {mypercent2 < 100 && industryDetails?.length > 0 && industryDetails.every((el) => el.isDisable) && (
                  <div style={{ display: addIndustryBtn }}>
                    <Button className="cj-add-new-skill-btn" onClick={() => {
                      setIndustryDetails((prev) => prev.map((el) => ({ ...el, isDisable: true })));
                      setAddIndustryInput("block");
                      setPercentage2("");
                    }}>
                      <Row><Col className="plus-Icon">+</Col><Col className="btn-Text">Add a new Industry</Col></Row>
                    </Button>
                  </div>
                )}
              </div>}
            </div>
          )}

          {/* Footer */}
          <div className="cj-footer">
            <label className="template-checkbox">
              <input
                type="checkbox"
                checked={saveAsTemplate}
                onChange={(e) => setSaveAsTemplate(e.target.checked)}
              />
              Save as Template
            </label>
            <div className="cj-footer-actions">
              {fromSignUp && (
                <button type="button" className="cj-skip-btn"
                  onClick={() => history.push({ pathname: "/allset", state: "allset" })}>
                  Skip, I'll post a job later
                </button>
              )}
              <button
                type="button"
                className={`cj-publish-btn${!isPublishEnabled ? " cj-publish-btn--inactive" : ""}`}
                onClick={handlePublish}
                disabled={isLoading || !isPublishEnabled}
              >
                {isLoading ? <LoadingOutlined style={{ fontSize: 16 }} /> : isCompletingIncompleteJob ? "Save and continue to bid" : <>{FromEdit ? "Save Changes" : <>Publish Job <span className="cj-send-icon">▶</span></>}</>}
              </button>
            </div>
          </div>

        </Form>
      </div>
    </div>
  );
}
